import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    MenuItem,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import { Title, useDataProvider, useNotify } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';

import {
    copyAcademicLiveExamTaskToCore,
    deleteLiveExamTaskVoice,
    deleteLiveExamTaskVoices,
    getLiveExamTaskVoiceRegenerationStatus,
    regenerateLiveExamTaskVoice,
    rewriteAdminAiContent,
    getLiveExamTaskFiles,
    getDescribeImageBlueprint,
    generateDescribeImageBlueprint,
    saveDescribeImageBlueprint,
    isExamTaskRecordActive,
    recreateLiveExamTaskImageFromUrl,
    resolveExamTaskImageAssetUrl,
    resolvePublishedExamTaskAssetUrl,
    resolveExamTaskVoiceAssetUrl,
    syncLiveExamTaskFilesToProduction,
    type AdminAiRewriteResponseDTO,
    type DescribeImageBlueprint,
    type DescribeImageBlueprintDetail,
    type LiveExamTaskFilesRecord,
    type LiveExamTaskVoiceRegenerationStatusRecord,
    type ExamTaskRecord,
} from '../admin/examTaskReviewDataProvider';
import { formatApiDateTime } from '../admin/dateTimeUtils';
import { useExamName } from '../admin/examName';
import { ExamTaskQuestionNoField, getExamTaskQuestionNo } from './ExamTaskQuestionNo';
import { MediaPreview, getMediaKind } from './MediaPreview';
import { SummarizeGroupDiscussionBlueprintPanel } from './SummarizeGroupDiscussionBlueprintPanel';
import { AiSourceArtifactPanel } from './AiSourceArtifactPanel';
import {
    getDefaultExamTaskTypeForExamName,
    getExamTaskTypeByKey,
    getExamTaskTypeLabel,
    isVoiceRequiredExamTaskType,
    isExamTaskTypeAvailableForExamName,
} from './examTaskTypes';
import {
    getExamTaskPublishTarget,
    getExamTaskWorkflow,
    getVisibleExamTaskWorkflowActions,
    type ExamTaskWorkflow,
    type ExamTaskWorkflowAction,
    type ExamTaskWorkflowId,
} from './examTaskWorkflows';

const publishActionButtonSx = {
    backgroundColor: '#7c3aed',
    borderColor: '#7c3aed',
    color: '#ffffff',
    '&:hover': {
        backgroundColor: '#6d28d9',
        borderColor: '#6d28d9',
    },
};

const useGenericAiSourceArtifactPanel = true;

const describeImageBlueprintTypes = [
    'bar_chart',
    'line_chart',
    'pie_chart',
    'table',
    'process_diagram',
    'map',
    'photograph_scene',
    'mixed_visual',
] as const;

function copyDescribeImageBlueprint(value: DescribeImageBlueprint): DescribeImageBlueprint {
    return {
        ...value,
        keyElements: [...value.keyElements],
        relationships: [...value.relationships],
        trends: [...value.trends],
        comparisons: [...value.comparisons],
        values: value.values.map((item) => ({ ...item })),
        safeImplications: [...value.safeImplications],
        unsupportedClaimWarnings: [...value.unsupportedClaimWarnings],
    };
}

function readBlueprintLines(value: string): string[] {
    return value.split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
}

function formatValue(value: unknown): string {
    if (value === undefined || value === null || value === '') return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
}

function formatColumnLabel(key: string): string {
    return key
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/^./, (char) => char.toUpperCase());
}

function isDescribeImageRecord(record: ExamTaskRecord | null, apiType?: string): boolean {
    if (apiType === 'DescribeImage') return true;
    return String(record?.examTaskType ?? '') === 'DescribeImage';
}

function isMediaUrlField(key: string, value: unknown): value is string {
    return /media.*url|image.*url/i.test(key) && typeof value === 'string' && value.trim().length > 0;
}

function isExamTaskActive(record: ExamTaskRecord | null): boolean {
    return isExamTaskRecordActive(record);
}

function resolveDescribeImageUrl(value: unknown): string {
    return resolveExamTaskImageAssetUrl(value);
}

function resolveVoiceUrl(value: unknown): string {
    return resolveExamTaskVoiceAssetUrl(value);
}

function getVoiceRecords(record: ExamTaskRecord | null): Array<Record<string, unknown>> {
    const voices = (record as Record<string, unknown> | null)?.voices;
    if (!Array.isArray(voices)) {
        return [];
    }

    return voices.filter((voice): voice is Record<string, unknown> => Boolean(voice) && typeof voice === 'object');
}

function getVoiceUrl(voice: Record<string, unknown>): string {
    return resolveVoiceUrl(voice.response_url ?? voice.responseUrl ?? voice.storageFilename ?? voice.StorageFilename ?? voice.url ?? voice.voiceUrl);
}

const complexityRankOptions = ['Easy', 'Medium', 'Hard'] as const;
const frequencyOptions = ['Weekly', 'Monthly', 'All'] as const;
const allowedDescribeImageExtensions = new Set(['jpg', 'jpeg', 'png', 'webp']);
const maxDescribeImageSizeInBytes = 10 * 1024 * 1024;
const describeImageAccept = '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp';
const describeImageOriginalCacheName = 'pteultra-admin:describe-image-originals';
const describeImageOriginalFileNameHeader = 'x-pteultra-original-file-name';

type LiveExamTaskFormState = {
    displayTitle: string;
    promptBody: string;
    complexityRank: string;
    freq: string;
    examTaskType: string;
    isPremiumContent: boolean;
    mediaResourceUrl: string;
    mediaCategory: string;
    discoveryTags: string;
    answerChoices: string;
    configSettings: string;
    examName: string;
    segments: string;
};

function toTextInputValue(value: unknown): string {
    if (value === undefined || value === null) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.join(', ');
    return JSON.stringify(value);
}

function toJsonEditorValue(value: unknown): string {
    if (value === undefined || value === null || value === '') return '';
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return '';
        try {
            return JSON.stringify(JSON.parse(trimmed), null, 2);
        } catch {
            return value;
        }
    }
    return JSON.stringify(value, null, 2);
}

function normalizeFrequencyValue(value: unknown): string {
    const normalized = String(value ?? '').trim().toLowerCase();

    if (normalized === 'weekly') return 'Weekly';
    if (normalized === 'monthly') return 'Monthly';
    if (normalized === 'all') return 'All';

    return '';
}

function buildLiveExamTaskFormState(record: ExamTaskRecord): LiveExamTaskFormState {
    const isDescribeImage = String(record.examTaskType ?? '') === 'DescribeImage';

    return {
        displayTitle: toTextInputValue(record.displayTitle ?? record.title ?? record.taskTitle ?? record.name),
        promptBody: toTextInputValue(record.promptBody ?? record.promptText ?? record.prompt ?? record.text),
        complexityRank: toTextInputValue(record.complexityRank),
        freq: normalizeFrequencyValue(record.freq ?? record.Freq),
        examTaskType: toTextInputValue(record.examTaskType),
        isPremiumContent: Boolean(record.isPremiumContent),
        mediaResourceUrl: toTextInputValue(
            isDescribeImage
                ? resolveDescribeImageUrl(record.mediaResourceUrl ?? record.mediaUrl ?? record.imageUrl)
                : record.mediaResourceUrl ?? record.mediaUrl ?? record.imageUrl,
        ),
        mediaCategory: toTextInputValue(record.mediaCategory),
        discoveryTags: toTextInputValue(record.discoveryTags),
        answerChoices: toJsonEditorValue(record.answerChoices),
        configSettings: toJsonEditorValue(record.configSettings),
        examName: toTextInputValue(record.examName),
        segments: toJsonEditorValue(record.segments),
    };
}

function parseJsonField(value: string, label: string): unknown | null {
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    try {
        return JSON.parse(trimmed);
    } catch {
        throw new Error(`${label} must be valid JSON`);
    }
}

function parseJsonOrStringField(value: string): unknown | null {
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    try {
        return JSON.parse(trimmed);
    } catch {
        return trimmed;
    }
}

function normalizeAnswerChoicesValue(value: string): string | null {
    const parsed = parseJsonOrStringField(value);
    if (parsed === null) {
        return null;
    }

    return typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
}

function normalizeFormDataKeySegment(value: string): string {
    return value
        .replace(/[_-]+([a-zA-Z0-9])/g, (_, character: string) => character.toUpperCase())
        .replace(/^./, (character) => character.toUpperCase());
}

function appendFormDataValue(formData: FormData, key: string, value: unknown): void {
    if (value === undefined || value === null || value === '') {
        return;
    }

    if (value instanceof File) {
        formData.append(key, value);
        return;
    }

    if (Array.isArray(value)) {
        value.forEach((item, index) => {
            appendFormDataValue(formData, `${key}[${index}]`, item);
        });
        return;
    }

    if (typeof value === 'object') {
        Object.entries(value as Record<string, unknown>).forEach(([childKey, childValue]) => {
            appendFormDataValue(formData, `${key}.${normalizeFormDataKeySegment(childKey)}`, childValue);
        });
        return;
    }

    formData.append(key, String(value));
}

function validateDescribeImageFile(file: File): string | null {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

    if (!allowedDescribeImageExtensions.has(extension)) {
        return 'Describe Image uploads must be JPG, JPEG, PNG, or WEBP files.';
    }

    if (file.size === 0 || file.size > maxDescribeImageSizeInBytes) {
        return 'Describe Image uploads must be between 1 byte and 10 MB.';
    }

    return null;
}

function formatStatusLabel(status: string): string {
    const trimmed = status.trim();
    if (!trimmed) {
        return 'Unknown';
    }

    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

function formatLocalDateTime(value: string | null | undefined): string {
    return formatApiDateTime(value, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'Australia/Sydney',
    });
}

function getDescribeImageOriginalCacheKey(examTaskId: string): string {
    return `https://pteultra-admin.local/live-exam-task/${encodeURIComponent(examTaskId)}/describe-image-original`;
}

function getFileExtensionFromMimeType(mimeType: string): string {
    const normalizedMimeType = mimeType.trim().toLowerCase();

    if (normalizedMimeType === 'image/jpeg' || normalizedMimeType === 'image/jpg') {
        return 'jpg';
    }

    if (normalizedMimeType === 'image/png') {
        return 'png';
    }

    if (normalizedMimeType === 'image/webp') {
        return 'webp';
    }

    return 'png';
}

function inferDescribeImageFileName(sourceUrl: string, contentType: string): string {
    try {
        const url = new URL(sourceUrl, window.location.origin);
        const candidate = url.pathname.split('/').pop()?.trim() ?? '';
        if (candidate && candidate.includes('.')) {
            return candidate;
        }
    } catch {
        const candidate = sourceUrl.split('/').pop()?.split('?')[0]?.trim() ?? '';
        if (candidate && candidate.includes('.')) {
            return candidate;
        }
    }

    return `describe-image-original.${getFileExtensionFromMimeType(contentType)}`;
}

function withImageRefreshToken(url: string, token: number): string {
    if (!url) {
        return url;
    }

    const parsedUrl = new URL(url, window.location.origin);
    parsedUrl.searchParams.set('t', String(token));
    return parsedUrl.toString();
}

async function hasCachedDescribeImageOriginal(examTaskId: string): Promise<boolean> {
    if (typeof window === 'undefined' || typeof window.caches === 'undefined') {
        return false;
    }

    const cache = await window.caches.open(describeImageOriginalCacheName);
    const response = await cache.match(getDescribeImageOriginalCacheKey(examTaskId));
    return Boolean(response);
}

async function cacheDescribeImageOriginal(examTaskId: string, sourceUrl: string): Promise<void> {
    if (typeof window === 'undefined' || typeof window.caches === 'undefined') {
        throw new Error('Browser cache is not available in this session.');
    }

    const cache = await window.caches.open(describeImageOriginalCacheName);
    const cacheKey = getDescribeImageOriginalCacheKey(examTaskId);
    const existing = await cache.match(cacheKey);
    if (existing) {
        return;
    }

    const response = await fetch(sourceUrl, { cache: 'no-store' });
    if (!response.ok) {
        throw new Error('Failed to download the original Describe Image asset.');
    }

    const blob = await response.blob();
    if (!blob.size) {
        throw new Error('The original Describe Image asset was empty.');
    }

    const contentType = blob.type || response.headers.get('content-type') || 'image/png';
    const cachedResponse = new Response(blob, {
        headers: {
            'Content-Type': contentType,
            [describeImageOriginalFileNameHeader]: inferDescribeImageFileName(sourceUrl, contentType),
        },
    });

    await cache.put(cacheKey, cachedResponse);
}

async function readCachedDescribeImageOriginal(examTaskId: string): Promise<File | null> {
    if (typeof window === 'undefined' || typeof window.caches === 'undefined') {
        return null;
    }

    const cache = await window.caches.open(describeImageOriginalCacheName);
    const response = await cache.match(getDescribeImageOriginalCacheKey(examTaskId));
    if (!response) {
        return null;
    }

    const blob = await response.blob();
    if (!blob.size) {
        return null;
    }

    const contentType = response.headers.get('Content-Type') || blob.type || 'image/png';
    const fileName = response.headers.get(describeImageOriginalFileNameHeader) || inferDescribeImageFileName('', contentType);
    return new File([blob], fileName, { type: contentType });
}

function buildLiveExamTaskPayload(form: LiveExamTaskFormState) {
    const segments = parseJsonField(form.segments, 'Segments');
    if (segments !== null && !Array.isArray(segments)) {
        throw new Error('Segments must be a JSON array');
    }

    return {
        displayTitle: form.displayTitle.trim(),
        promptBody: form.promptBody.trim(),
        complexityRank: form.complexityRank.trim(),
        freq: form.freq.trim() || null,
        examTaskType: form.examTaskType.trim(),
        isPremiumContent: form.isPremiumContent,
        mediaResourceUrl: form.mediaResourceUrl.trim() || null,
        mediaCategory: form.mediaCategory.trim() || null,
        discoveryTags: form.discoveryTags.trim(),
        answerChoices: normalizeAnswerChoicesValue(form.answerChoices),
        configSettings: parseJsonField(form.configSettings, 'Config settings'),
        examName: form.examName.trim(),
        segments: segments ?? [],
    };
}

function buildLiveExamTaskFormData(form: LiveExamTaskFormState, describeImageFile: File | null): FormData {
    const payload = buildLiveExamTaskPayload(form);
    const formData = new FormData();

    appendFormDataValue(formData, 'DisplayTitle', payload.displayTitle);
    appendFormDataValue(formData, 'PromptBody', payload.promptBody);
    appendFormDataValue(formData, 'ComplexityRank', payload.complexityRank);
    appendFormDataValue(formData, 'Freq', payload.freq);
    appendFormDataValue(formData, 'ExamTaskType', payload.examTaskType);
    appendFormDataValue(formData, 'IsPremiumContent', payload.isPremiumContent);
    appendFormDataValue(formData, 'MediaResourceUrl', payload.mediaResourceUrl);
    appendFormDataValue(formData, 'MediaCategory', payload.mediaCategory);
    appendFormDataValue(formData, 'DiscoveryTags', payload.discoveryTags);
    appendFormDataValue(formData, 'AnswerChoices', payload.answerChoices);
    appendFormDataValue(formData, 'ConfigSettings', payload.configSettings);
    appendFormDataValue(formData, 'ExamName', payload.examName);
    appendFormDataValue(formData, 'Segments', payload.segments);

    if (describeImageFile) {
        formData.append('DescribeImageFile', describeImageFile);
    }

    return formData;
}

type ExamTaskDetailPageProps = {
    workflow?: ExamTaskWorkflowId;
};

type ExamTaskDetailContentProps = {
    workflow: ExamTaskWorkflow;
    examTaskType?: string;
    examTaskId?: string;
    onReviewActionCompleted?: () => void;
    onRecordDeleted?: () => void;
};

type PublishedFilePreview = {
    id: string;
    label: string;
    storageKey: string;
    previewUrl: string;
};

function getPublishedVoicePreviews(files: LiveExamTaskFilesRecord | null): PublishedFilePreview[] {
    if (!files) {
        return [];
    }

    return files.voices
        .filter((voice) => typeof voice.publishedFileKey === 'string' && voice.publishedFileKey.trim().length > 0)
        .map((voice) => ({
            id: voice.examTaskMediaVoiceId,
            label: voice.voiceLabel || 'Voice',
            storageKey: String(voice.publishedFileKey),
            previewUrl: resolvePublishedExamTaskAssetUrl(voice.publishedFileKey),
        }));
}

export function ExamTaskDetailContent({
    workflow,
    examTaskType,
    examTaskId,
    onReviewActionCompleted,
    onRecordDeleted,
}: ExamTaskDetailContentProps) {
    const { type: routeType, id: routeId } = useParams<{ type: string; id: string }>();
    const navigate = useNavigate();
    const publishTarget = getExamTaskPublishTarget();
    const type = examTaskType ?? routeType;
    const id = examTaskId ?? routeId;
    const match = getExamTaskTypeByKey(type);
    const environment = String(import.meta.env.VITE_ENVMENT ?? '').trim().toLowerCase();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const { examName, setExamName } = useExamName();
    const currentExamTaskLabel = getExamTaskTypeLabel(match, examName);
    const canCopyAcademicToCore = workflow.id === 'destination'
        && examName === 'Academic'
        && Boolean(match)
        && isExamTaskTypeAvailableForExamName(match?.key, 'Core');
    const canManageVoiceGeneration = environment !== 'stage' && environment !== 'prod';

    const [record, setRecord] = useState<ExamTaskRecord | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionKey, setActionKey] = useState<string | null>(null);
    const [liveForm, setLiveForm] = useState<LiveExamTaskFormState | null>(null);
    const [describeImageFile, setDescribeImageFile] = useState<File | null>(null);
    const [describeImagePreviewUrl, setDescribeImagePreviewUrl] = useState<string | null>(null);
    const [describeImageRefreshToken, setDescribeImageRefreshToken] = useState(() => Date.now());
    const [hasCachedOriginalDescribeImage, setHasCachedOriginalDescribeImage] = useState(false);
    const [liveFiles, setLiveFiles] = useState<LiveExamTaskFilesRecord | null>(null);
    const [liveFilesLoading, setLiveFilesLoading] = useState(false);
    const [liveFilesError, setLiveFilesError] = useState<string | null>(null);
    const [publishingFiles, setPublishingFiles] = useState(false);
    const [pendingPublishAction, setPendingPublishAction] = useState<ExamTaskWorkflowAction | null>(null);
    const [recreatingDescribeImage, setRecreatingDescribeImage] = useState(false);
    const [revertingDescribeImage, setRevertingDescribeImage] = useState(false);
    const [generateContentDialogOpen, setGenerateContentDialogOpen] = useState(false);
    const [generateContentIsConservative, setGenerateContentIsConservative] = useState(false);
    const [generatingContent, setGeneratingContent] = useState(false);
    const [generatedContent, setGeneratedContent] = useState<AdminAiRewriteResponseDTO | null>(null);
    const [generatedContentError, setGeneratedContentError] = useState<string | null>(null);
    const [copyToCoreDialogOpen, setCopyToCoreDialogOpen] = useState(false);
    const [voiceRegenerationStatus, setVoiceRegenerationStatus] = useState<LiveExamTaskVoiceRegenerationStatusRecord | null>(null);
    const [voiceRegenerationStatusLoading, setVoiceRegenerationStatusLoading] = useState(false);
    const [voiceRegenerationStatusError, setVoiceRegenerationStatusError] = useState<string | null>(null);
    const [voiceRegenerationStatusPolling, setVoiceRegenerationStatusPolling] = useState(false);
    const [blueprintDetail, setBlueprintDetail] = useState<DescribeImageBlueprintDetail | null>(null);
    const [blueprintDraft, setBlueprintDraft] = useState<DescribeImageBlueprint | null>(null);
    const [blueprintNotes, setBlueprintNotes] = useState('');
    const [blueprintLoading, setBlueprintLoading] = useState(false);
    const [blueprintAction, setBlueprintAction] = useState<'generate' | 'regenerate' | 'save' | null>(null);
    const [blueprintError, setBlueprintError] = useState<string | null>(null);

    useEffect(() => {
        if (!describeImageFile) {
            setDescribeImagePreviewUrl(null);
            return;
        }

        const objectUrl = window.URL.createObjectURL(describeImageFile);
        setDescribeImagePreviewUrl(objectUrl);

        return () => {
            window.URL.revokeObjectURL(objectUrl);
        };
    }, [describeImageFile]);

    useEffect(() => {
        const taskId = String(record?.id ?? '').trim();
        if (useGenericAiSourceArtifactPanel || !taskId || !isDescribeImageRecord(record, match?.apiType)) {
            setBlueprintDetail(null);
            setBlueprintDraft(null);
            setBlueprintError(null);
            return;
        }

        let cancelled = false;
        setBlueprintLoading(true);
        setBlueprintError(null);
        void getDescribeImageBlueprint(taskId, examName)
            .then((detail) => {
                if (cancelled) return;
                setBlueprintDetail(detail);
                setBlueprintDraft(detail.artifact ? copyDescribeImageBlueprint(detail.artifact.payload) : null);
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                setBlueprintError(error instanceof Error ? error.message : 'Failed to load the Describe Image blueprint.');
            })
            .finally(() => {
                if (!cancelled) setBlueprintLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [examName, match?.apiType, record]);

    const isLiveWorkflow = workflow.id === 'destination';
    const primaryAction = workflow.actions.find((action) => action.key === 'primary') ?? null;
    const publishedImageUrl = liveFiles?.publishedImageKey
        ? resolvePublishedExamTaskAssetUrl(liveFiles.publishedImageKey)
        : '';
    const publishedVoicePreviews = useMemo(() => getPublishedVoicePreviews(liveFiles), [liveFiles]);

    const describeImagePreviewSourceUrl =
        isDescribeImageRecord(record, match?.apiType) && liveForm?.mediaResourceUrl.trim()
            ? withImageRefreshToken(resolveDescribeImageUrl(liveForm.mediaResourceUrl.trim()), describeImageRefreshToken)
            : '';
    const describeImageDownloadUrl =
        isDescribeImageRecord(record, match?.apiType) && liveForm?.mediaResourceUrl.trim()
            ? resolveDescribeImageUrl(liveForm.mediaResourceUrl.trim())
            : resolveDescribeImageUrl(record?.mediaResourceUrl ?? record?.mediaUrl ?? record?.imageUrl);

    const refreshExamTaskDetail = async (
        examTaskId: string,
        options?: {
            preserveDescribeImageDraft?: boolean;
            refreshLiveFiles?: boolean;
        },
    ) => {
        const response = await dataProvider.getOne(workflow.resource, { id: examTaskId });
        const nextRecord = response.data as ExamTaskRecord;

        setRecord(nextRecord);

        if (isLiveWorkflow) {
            if (options?.preserveDescribeImageDraft && isDescribeImageRecord(nextRecord, match?.apiType)) {
                const nextFormState = buildLiveExamTaskFormState(nextRecord);
                setLiveForm((current) => {
                    if (!current) {
                        return nextFormState;
                    }

                    return {
                        ...current,
                        mediaResourceUrl: nextFormState.mediaResourceUrl,
                        mediaCategory: nextFormState.mediaCategory,
                    };
                });
            } else {
                setLiveForm(buildLiveExamTaskFormState(nextRecord));
            }
        }

        if (options?.refreshLiveFiles && isLiveWorkflow) {
            await refreshLiveFiles(String(nextRecord.id), { silent: true });
        }

        return nextRecord;
    };

    const refreshLiveFiles = async (examTaskId: string, options?: { silent?: boolean }) => {
        if (!isLiveWorkflow) {
            setLiveFiles(null);
            setLiveFilesError(null);
            setLiveFilesLoading(false);
            return null;
        }

        setLiveFilesLoading(true);
        setLiveFilesError(null);

        try {
            const nextFiles = await getLiveExamTaskFiles(examTaskId);
            setLiveFiles(nextFiles);
            return nextFiles;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to load published files';
            setLiveFiles(null);
            setLiveFilesError(message);

            if (!options?.silent) {
                notify(message, { type: 'warning' });
            }

            return null;
        } finally {
            setLiveFilesLoading(false);
        }
    };

    const handleGenerateBlueprint = async (force: boolean) => {
        const taskId = String(record?.id ?? '').trim();
        if (!taskId) return;

        setBlueprintAction(force ? 'regenerate' : 'generate');
        setBlueprintError(null);
        try {
            const artifact = await generateDescribeImageBlueprint(taskId, examName, force);
            setBlueprintDetail((current) => ({
                artifact,
                mediaResourceUrl: current?.mediaResourceUrl ?? String(record?.mediaResourceUrl ?? ''),
                promptBody: current?.promptBody ?? String(record?.promptBody ?? ''),
            }));
            setBlueprintDraft(copyDescribeImageBlueprint(artifact.payload));
            notify(force ? 'Blueprint regenerated as a candidate.' : 'Blueprint candidate generated.', { type: 'success' });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Blueprint generation failed.';
            setBlueprintError(message);
            notify(message, { type: 'error' });
        } finally {
            setBlueprintAction(null);
        }
    };

    const handleSaveBlueprint = async () => {
        const artifactId = blueprintDetail?.artifact?.artifactId;
        if (!artifactId || !blueprintDraft) return;

        setBlueprintAction('save');
        setBlueprintError(null);
        try {
            const artifact = await saveDescribeImageBlueprint(artifactId, blueprintDraft, blueprintNotes);
            setBlueprintDetail((current) => current ? { ...current, artifact } : current);
            setBlueprintDraft(copyDescribeImageBlueprint(artifact.payload));
            setBlueprintNotes('');
            notify('Blueprint saved and approved.', { type: 'success' });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Blueprint save failed.';
            setBlueprintError(message);
            notify(message, { type: 'error' });
        } finally {
            setBlueprintAction(null);
        }
    };

    const setBlueprintList = (
        field: 'keyElements' | 'relationships' | 'trends' | 'comparisons' | 'safeImplications' | 'unsupportedClaimWarnings',
        value: string,
    ) => {
        setBlueprintDraft((current) => current ? { ...current, [field]: readBlueprintLines(value) } : current);
    };

    useEffect(() => {
        if (!match || isExamTaskTypeAvailableForExamName(match.key, examName)) {
            return;
        }

        const fallbackExamTaskType = getDefaultExamTaskTypeForExamName(examName);
        if (!fallbackExamTaskType) {
            return;
        }

        notify(`${currentExamTaskLabel} is not available for ${examName}. Showing ${getExamTaskTypeLabel(fallbackExamTaskType, examName)} instead.`, {
            type: 'info',
        });

        if (examTaskType) {
            onRecordDeleted?.();
            return;
        }

        navigate(`${workflow.routeBase}/${fallbackExamTaskType.key}`, { replace: true });
    }, [currentExamTaskLabel, examName, examTaskType, match, navigate, notify, onRecordDeleted, workflow.routeBase]);

    useEffect(() => {
        if (!id || !isLiveWorkflow) {
            setHasCachedOriginalDescribeImage(false);
            return;
        }

        let active = true;

        void hasCachedDescribeImageOriginal(id)
            .then((cached) => {
                if (active) {
                    setHasCachedOriginalDescribeImage(cached);
                }
            })
            .catch(() => {
                if (active) {
                    setHasCachedOriginalDescribeImage(false);
                }
            });

        return () => {
            active = false;
        };
    }, [id, isLiveWorkflow]);

    useEffect(() => {
        if (!id || workflow.missingControllerMessage) {
            setRecord(null);
            setLiveFiles(null);
            setLiveFilesError(null);
            setHasCachedOriginalDescribeImage(false);
            setVoiceRegenerationStatus(null);
            setVoiceRegenerationStatusError(null);
            setVoiceRegenerationStatusPolling(false);
            setLoading(false);
            return;
        }

        if (!id) return;

        let active = true;

        const load = async () => {
            setLoading(true);
            try {
                const response = await dataProvider.getOne(workflow.resource, { id });
                if (!active) return;
                const nextRecord = response.data as ExamTaskRecord;
                setRecord(nextRecord);
                setLiveForm(isLiveWorkflow ? buildLiveExamTaskFormState(nextRecord) : null);
                setDescribeImageFile(null);

                if (isLiveWorkflow) {
                    setLiveFilesLoading(true);
                    setLiveFilesError(null);

                    try {
                        const nextFiles = await getLiveExamTaskFiles(String(nextRecord.id));
                        if (!active) return;
                        setLiveFiles(nextFiles);
                    } catch (error) {
                        if (!active) return;
                        setLiveFiles(null);
                        setLiveFilesError(error instanceof Error ? error.message : 'Failed to load published files');
                    } finally {
                        if (active) {
                            setLiveFilesLoading(false);
                        }
                    }
                } else {
                    setLiveFiles(null);
                    setLiveFilesError(null);
                    setLiveFilesLoading(false);
                }
            } catch (error) {
                if (!active) return;
                notify(error instanceof Error ? error.message : 'Failed to load exam task detail', {
                    type: 'error',
                });
                setRecord(null);
                setLiveForm(null);
                setDescribeImageFile(null);
                setLiveFiles(null);
                setLiveFilesError(null);
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        void load();

        return () => {
            active = false;
        };
    }, [dataProvider, id, isLiveWorkflow, notify, workflow.missingControllerMessage, workflow.resource]);

    useEffect(() => {
        if (!voiceRegenerationStatusPolling || !record) {
            return;
        }

        let active = true;

        const fetchStatus = async () => {
            setVoiceRegenerationStatusLoading(true);

            try {
                const nextStatus = await getLiveExamTaskVoiceRegenerationStatus(String(record.id));
                if (!active) {
                    return;
                }

                setVoiceRegenerationStatus(nextStatus);
                setVoiceRegenerationStatusError(null);

                 if (nextStatus.isTerminal) {
                    setVoiceRegenerationStatusPolling(false);

                    if (nextStatus.requestStatus === 'succeeded') {
                        await refreshExamTaskDetail(String(record.id), { refreshLiveFiles: true });
                    }
                }
            } catch (error) {
                if (!active) {
                    return;
                }

                setVoiceRegenerationStatusError(error instanceof Error ? error.message : 'Failed to load audio regeneration status.');
                setVoiceRegenerationStatusPolling(false);
            } finally {
                if (active) {
                    setVoiceRegenerationStatusLoading(false);
                }
            }
        };

        void fetchStatus();
        const intervalId = window.setInterval(() => {
            void fetchStatus();
        }, 10000);

        return () => {
            active = false;
            window.clearInterval(intervalId);
        };
    }, [record, voiceRegenerationStatusPolling]);

    const rowEntries = useMemo(() => {
        if (!record) return [] as Array<[string, unknown]>;
        return Object.entries(record).sort(([left], [right]) => left.localeCompare(right));
    }, [record]);

    const voiceRecords = useMemo(() => getVoiceRecords(record), [record]);
    const shouldShowVoiceSection = useMemo(
        () => voiceRecords.length > 0 || isVoiceRequiredExamTaskType(String(match?.apiType ?? record?.examTaskType ?? '').trim()),
        [match?.apiType, record?.examTaskType, voiceRecords.length],
    );
    const questionNo = useMemo(() => getExamTaskQuestionNo(record), [record]);

    const handleQuestionNoCopied = (value: string) => {
        notify(`Question No copied: ${value}`, { type: 'info' });
    };

    const handleQuestionNoCopyError = (error: unknown) => {
        notify(error instanceof Error ? error.message : 'Unable to copy Question No.', { type: 'warning' });
    };

    const handleReviewAction = async (action: ExamTaskWorkflowAction) => {
        if (!record) return;

        if (!action.actionPath) {
            notify('This exam task action is not configured in Project Settings.', {
                type: 'warning',
            });
            return;
        }

        setActionKey(action.key);
        try {
            await dataProvider.update(workflow.resource, {
                id: record.id,
                data: {},
                previousData: record,
                meta: {
                    action: action.key,
                    actionPath: action.actionPath,
                },
            });

            notify(action.successMessage(currentExamTaskLabel ?? 'Exam Task'), { type: 'info' });

            if (workflow.id === 'review') {
                onReviewActionCompleted?.();
                return;
            }

            const response = await dataProvider.getOne(workflow.resource, { id: record.id });
            setRecord(response.data as ExamTaskRecord);
        } catch (error) {
            notify(error instanceof Error ? error.message : action.errorMessage, {
                type: 'error',
            });
        } finally {
            setActionKey(null);
        }
    };

    const handleLiveFieldChange = (field: keyof LiveExamTaskFormState, value: string | boolean) => {
        setLiveForm((current) => {
            if (!current) return current;
            return {
                ...current,
                [field]: value,
            };
        });
    };

    const handleLiveSave = async () => {
        if (!record || !liveForm || !primaryAction) {
            return;
        }

        if (!primaryAction.actionPath) {
            notify('This exam task action is not configured in Project Settings.', {
                type: 'warning',
            });
            return;
        }

        setActionKey(primaryAction.key);
        try {
            const payload = buildLiveExamTaskFormData(liveForm, describeImageFile);
            const response = await dataProvider.update(workflow.resource, {
                id: record.id,
                data: payload,
                previousData: record,
                meta: {
                    action: primaryAction.key,
                    actionPath: primaryAction.actionPath,
                },
            });

            const nextRecord = response.data as ExamTaskRecord;
            setRecord(nextRecord);
            setLiveForm(buildLiveExamTaskFormState(nextRecord));
            setDescribeImageFile(null);
            await refreshLiveFiles(String(nextRecord.id), { silent: true });
            notify(primaryAction.successMessage(currentExamTaskLabel ?? 'Exam Task'), { type: 'info' });
        } catch (error) {
            notify(error instanceof Error ? error.message : primaryAction.errorMessage, {
                type: 'error',
            });
        } finally {
            setActionKey(null);
        }
    };

    const refreshLiveVoiceState = async (examTaskId: string) => {
        const response = await dataProvider.getOne(workflow.resource, { id: examTaskId });
        setRecord(response.data as ExamTaskRecord);
        await refreshLiveFiles(examTaskId, { silent: true });
    };

    const handleDeleteLiveVoice = async (voice: Record<string, unknown>) => {
        if (!record) {
            return;
        }

        const voiceId = String(voice.examTaskMediaVoiceId ?? voice.id ?? '').trim();
        if (!voiceId) {
            notify('This audio file cannot be deleted because its voice id is missing.', { type: 'warning' });
            return;
        }

        const voiceLabel = String(voice.voiceLabel ?? voice.label ?? voice.name ?? voice.StorageFilename ?? 'this audio file');
        const confirmed = window.confirm(`Delete ${voiceLabel} from this live exam task?`);
        if (!confirmed) {
            return;
        }

        setActionKey(`delete-voice:${voiceId}`);
        try {
            await deleteLiveExamTaskVoice(String(record.id), voiceId);
            await refreshLiveVoiceState(String(record.id));
            notify('Audio deleted.', { type: 'info' });
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to delete audio.', {
                type: 'error',
            });
        } finally {
            setActionKey(null);
        }
    };

    const handleDeleteAllLiveVoices = async () => {
        if (!record || voiceRecords.length === 0) {
            return;
        }

        const confirmed = window.confirm('Delete all audio files from this live exam task?');
        if (!confirmed) {
            return;
        }

        setActionKey('delete-voices');
        try {
            await deleteLiveExamTaskVoices(String(record.id));
            await refreshLiveVoiceState(String(record.id));
            notify('All audio deleted.', { type: 'info' });
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to delete all audio.', {
                type: 'error',
            });
        } finally {
            setActionKey(null);
        }
    };

    const handleRegenerateLiveVoice = async () => {
        if (!record) {
            return;
        }

        setActionKey('regenerate-voice');
        try {
            await regenerateLiveExamTaskVoice(String(record.id));
            setVoiceRegenerationStatus(null);
            setVoiceRegenerationStatusError(null);
            setVoiceRegenerationStatusPolling(false);
            notify('Audio regeneration queued.', { type: 'info' });
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to queue audio regeneration.', {
                type: 'error',
            });
        } finally {
            setActionKey(null);
        }
    };

    const handleRefreshLiveVoiceStatus = async () => {
        if (!record) {
            return;
        }

        setVoiceRegenerationStatusError(null);
        setVoiceRegenerationStatusPolling(true);
    };

    const handleRecreateDescribeImage = async () => {
        if (!record || !liveForm || !isDescribeImageRecord(record, match?.apiType)) {
            return;
        }

        const sourceImageUrl = resolveDescribeImageUrl(record.mediaResourceUrl ?? record.mediaUrl ?? record.imageUrl);
        if (!sourceImageUrl) {
            notify('No Describe Image source is available for this exam task.', { type: 'warning' });
            return;
        }

        setRecreatingDescribeImage(true);
        try {
            await cacheDescribeImageOriginal(String(record.id), sourceImageUrl);
            setHasCachedOriginalDescribeImage(true);

            await recreateLiveExamTaskImageFromUrl(String(record.id), sourceImageUrl);
            await refreshExamTaskDetail(String(record.id), {
                preserveDescribeImageDraft: true,
                refreshLiveFiles: true,
            });
            setDescribeImageRefreshToken(Date.now());
            notify('Describe Image regenerated successfully.', { type: 'info' });
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to regenerate the Describe Image.', {
                type: 'error',
            });
        } finally {
            setRecreatingDescribeImage(false);
        }
    };

    const handleRevertDescribeImage = async () => {
        if (!record || !primaryAction || !primaryAction.actionPath) {
            return;
        }

        const cachedOriginalFile = await readCachedDescribeImageOriginal(String(record.id));
        if (!cachedOriginalFile) {
            notify('No cached original Describe Image is available to restore.', { type: 'warning' });
            return;
        }

        const validationMessage = validateDescribeImageFile(cachedOriginalFile);
        if (validationMessage) {
            notify(validationMessage, { type: 'error' });
            return;
        }

        setRevertingDescribeImage(true);
        try {
            const payload = buildLiveExamTaskFormData(buildLiveExamTaskFormState(record), cachedOriginalFile);
            await dataProvider.update(workflow.resource, {
                id: record.id,
                data: payload,
                previousData: record,
                meta: {
                    action: primaryAction.key,
                    actionPath: primaryAction.actionPath,
                },
            });

            setDescribeImageFile(null);
            await refreshExamTaskDetail(String(record.id), {
                preserveDescribeImageDraft: true,
                refreshLiveFiles: true,
            });
            setDescribeImageRefreshToken(Date.now());
            notify('Original Describe Image restored.', { type: 'info' });
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to restore the original Describe Image.', {
                type: 'error',
            });
        } finally {
            setRevertingDescribeImage(false);
        }
    };

    const runLiveStateAction = async (action: ExamTaskWorkflowAction) => {
        if (!record) {
            return;
        }

        if (!action.actionPath) {
            notify('This exam task action is not configured in Project Settings.', {
                type: 'warning',
            });
            return;
        }

        if (action.requiresConfirmation) {
            const confirmed = window.confirm(
                action.confirmationMessage?.(String(record.displayTitle ?? record.id)) ??
                    'Are you sure you want to run this action?',
            );
            if (!confirmed) {
                return;
            }
        }

        setActionKey(action.key);
        try {
            await dataProvider.update(workflow.resource, {
                id: record.id,
                data: {},
                previousData: record,
                meta: {
                    action: action.key,
                    actionPath: action.actionPath,
                    method: action.requestMethod,
                },
            });

            notify(action.successMessage(currentExamTaskLabel ?? 'Exam Task'), { type: 'info' });

            if (action.removesRecord) {
                if (examTaskType) {
                    onRecordDeleted?.();
                    return;
                }

                navigate(match ? `${workflow.routeBase}/${match.key}` : workflow.routeBase, { replace: true });
                return;
            }

            const response = await dataProvider.getOne(workflow.resource, { id: record.id });
            const nextRecord = response.data as ExamTaskRecord;
            setRecord(nextRecord);
            setLiveForm(buildLiveExamTaskFormState(nextRecord));
            await refreshLiveFiles(String(nextRecord.id), { silent: true });
        } catch (error) {
            notify(error instanceof Error ? error.message : action.errorMessage, {
                type: 'error',
            });
        } finally {
            setActionKey(null);
        }
    };

    const handleLiveStateAction = async (action: ExamTaskWorkflowAction) => {
        if (action.key === 'publish') {
            setPendingPublishAction(action);
            return;
        }

        await runLiveStateAction(action);
    };

    const handleDownloadImage = (sourceUrl = describeImageDownloadUrl) => {
        const mediaUrl = typeof sourceUrl === 'string' ? sourceUrl.trim() : '';
        if (typeof mediaUrl !== 'string' || !mediaUrl) {
            notify('No image is available for this exam task.', { type: 'warning' });
            return;
        }

        if (getMediaKind(mediaUrl) !== 'image') {
            notify('The media resource for this exam task is not an image.', { type: 'warning' });
            return;
        }

        const openedWindow = window.open(mediaUrl, '_blank', 'noopener,noreferrer');
        if (!openedWindow) {
            notify('Unable to open the image in a new tab. Check whether the browser blocked the popup.', {
                type: 'warning',
            });
        }
    };

    const handlePublishFiles = async () => {
        if (!record) {
            return;
        }

        setPublishingFiles(true);
        try {
            const nextFiles = await syncLiveExamTaskFilesToProduction(String(record.id));
            setLiveFiles(nextFiles);
            setLiveFilesError(null);
            notify('Exam task files published', { type: 'info' });
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to publish exam task files', {
                type: 'error',
            });
        } finally {
            setPublishingFiles(false);
        }
    };

    const handleRequestCopyAcademicToCore = () => {
        setCopyToCoreDialogOpen(true);
    };

    const handleCopyAcademicToCore = async () => {
        if (!record || !match || !canCopyAcademicToCore) {
            return;
        }

        setActionKey('copyAcademicToCore');
        try {
            const copiedRecord = await copyAcademicLiveExamTaskToCore(String(record.id));
            setExamName('Core');
            notify(`${String(record.displayTitle ?? currentExamTaskLabel ?? 'Exam task')} copied to Core.`, {
                type: 'info',
            });
            navigate(`${workflow.routeBase}/${match.key}/${String(copiedRecord.id)}`);
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Failed to copy the Academic exam task to Core.', {
                type: 'error',
            });
        } finally {
            setActionKey(null);
        }
    };

    const handleOpenGenerateContentDialog = () => {
        if (!liveForm?.promptBody.trim()) {
            notify('Prompt Body is required before generating content.', { type: 'warning' });
            return;
        }

        setGenerateContentDialogOpen(true);
        setGeneratedContent(null);
        setGeneratedContentError(null);
    };

    const handleCloseGenerateContentDialog = () => {
        if (generatingContent) {
            return;
        }

        setGenerateContentDialogOpen(false);
        setGeneratedContent(null);
        setGeneratedContentError(null);
    };

    const handleGenerateContent = async () => {
        if (!liveForm) {
            return;
        }

        const paragraph = liveForm.promptBody.trim();
        if (!paragraph) {
            notify('Prompt Body is required before generating content.', { type: 'warning' });
            return;
        }

        setGeneratedContent(null);
        setGeneratedContentError(null);
        setGeneratingContent(true);

        try {
            const nextGeneratedContent = await rewriteAdminAiContent({
                paragraph,
                IsConservative: generateContentIsConservative,
            });
            setGeneratedContent(nextGeneratedContent);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to generate exam task content';
            setGeneratedContentError(message);
            notify(message, { type: 'error' });
        } finally {
            setGeneratingContent(false);
        }
    };

    const handleApplyGeneratedContent = () => {
        if (!generatedContent) {
            return;
        }

        setLiveForm((current) => {
            if (!current) {
                return current;
            }

            return {
                ...current,
                displayTitle: generatedContent.suggestedTitle,
                promptBody: generatedContent.rewrittenParagraph,
            };
        });
        setGenerateContentDialogOpen(false);
        setGeneratedContent(null);
        setGeneratedContentError(null);
        notify('Generated content applied to the form. Save to persist the changes.', { type: 'info' });
    };

    return (
        <Stack spacing={3}>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
            >
                <Box>
                    <Typography variant="h5" gutterBottom>
                        {currentExamTaskLabel ?? type ?? 'Exam Task Detail'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {workflow.detailDescription(id)}
                    </Typography>
                    <ExamTaskQuestionNoField
                        value={questionNo}
                        valueVariant="body1"
                        onCopied={handleQuestionNoCopied}
                        onCopyError={handleQuestionNoCopyError}
                        sx={{ mt: 1.25 }}
                    />
                </Box>

                {isLiveWorkflow ? (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        {canCopyAcademicToCore ? (
                            <Button
                                variant="contained"
                                disabled={!record || Boolean(actionKey)}
                                onClick={handleRequestCopyAcademicToCore}
                            >
                                    {actionKey === 'copyAcademicToCore' ? 'Copying...' : 'Copy to Core'}
                            </Button>
                        ) : null}
                        <Button
                            variant="outlined"
                            disabled={!record || !liveForm?.promptBody.trim() || generatingContent || Boolean(actionKey)}
                            onClick={handleOpenGenerateContentDialog}
                        >
                            {generatingContent ? 'Generating Content...' : 'Generate Content'}
                        </Button>
                        <Button
                            variant="contained"
                            disabled={!record || publishingFiles || Boolean(actionKey)}
                            onClick={() => void handlePublishFiles()}
                        >
                            {publishingFiles ? 'Publishing Files...' : 'Publish Files'}
                        </Button>
                    </Stack>
                ) : null}
            </Stack>

            {workflow.missingControllerMessage ? (
                <Alert severity="warning">{workflow.missingControllerMessage}</Alert>
            ) : loading ? (
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                        Loading exam task detail...
                    </Typography>
                </Stack>
            ) : !record ? (
                <Alert severity="error">Exam task detail could not be loaded.</Alert>
            ) : isLiveWorkflow && liveForm ? (
                <>
                    <Stack spacing={2}>
                        <TextField
                            label="Display Title"
                            value={liveForm.displayTitle}
                            onChange={(event) => handleLiveFieldChange('displayTitle', event.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Prompt Body"
                            value={liveForm.promptBody}
                            onChange={(event) => handleLiveFieldChange('promptBody', event.target.value)}
                            multiline
                            minRows={4}
                            fullWidth
                        />
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                                label="Complexity Rank"
                                select
                                value={liveForm.complexityRank}
                                onChange={(event) => handleLiveFieldChange('complexityRank', event.target.value)}
                                fullWidth
                            >
                                {complexityRankOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Frequency"
                                select
                                value={liveForm.freq}
                                onChange={(event) => handleLiveFieldChange('freq', event.target.value)}
                                fullWidth
                            >
                                <MenuItem value="">Not set</MenuItem>
                                {frequencyOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                label="Exam Task Type"
                                value={liveForm.examTaskType}
                                onChange={(event) => handleLiveFieldChange('examTaskType', event.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Exam Name"
                                value={liveForm.examName}
                                onChange={(event) => handleLiveFieldChange('examName', event.target.value)}
                                fullWidth
                            />
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                                label="Media Resource URL"
                                value={liveForm.mediaResourceUrl}
                                onChange={(event) => handleLiveFieldChange('mediaResourceUrl', event.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Media Category"
                                value={liveForm.mediaCategory}
                                onChange={(event) => handleLiveFieldChange('mediaCategory', event.target.value)}
                                fullWidth
                            />
                        </Stack>
                        {liveForm.mediaResourceUrl.trim() ? (
                            <MediaPreview
                                url={isDescribeImageRecord(record, match?.apiType) ? describeImagePreviewSourceUrl : liveForm.mediaResourceUrl.trim()}
                                title={liveForm.displayTitle || currentExamTaskLabel || 'Exam Task Media'}
                                width="100%"
                                height={220}
                                showUrl
                            />
                        ) : null}
                        {isDescribeImageRecord(record, match?.apiType) ? (
                            <Stack spacing={1.5}>
                                <Typography variant="subtitle2">Describe Image Upload</Typography>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap">
                                    <Button component="label" variant="outlined" startIcon={<CloudUploadOutlinedIcon />}>
                                        {describeImageFile ? 'Replace Image File' : 'Choose Image File'}
                                        <input
                                            hidden
                                            type="file"
                                            accept={describeImageAccept}
                                            onChange={(event) => {
                                                const nextFile = event.target.files?.[0] ?? null;
                                                event.target.value = '';

                                                if (!nextFile) {
                                                    return;
                                                }

                                                const validationMessage = validateDescribeImageFile(nextFile);
                                                if (validationMessage) {
                                                    notify(validationMessage, { type: 'error' });
                                                    return;
                                                }

                                                setDescribeImageFile(nextFile);
                                            }}
                                        />
                                    </Button>
                                    <Button
                                        variant="text"
                                        color="inherit"
                                        startIcon={<DeleteOutlineIcon />}
                                        onClick={() => setDescribeImageFile(null)}
                                        disabled={!describeImageFile || recreatingDescribeImage || revertingDescribeImage}
                                    >
                                        Clear Selected File
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<AutoFixHighOutlinedIcon />}
                                        onClick={() => void handleRecreateDescribeImage()}
                                        disabled={
                                            Boolean(actionKey) ||
                                            recreatingDescribeImage ||
                                            revertingDescribeImage ||
                                            !record ||
                                            !resolveDescribeImageUrl(record.mediaResourceUrl ?? record.mediaUrl ?? record.imageUrl)
                                        }
                                    >
                                        {recreatingDescribeImage ? 'Generating Image...' : 'Generate Image'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="inherit"
                                        startIcon={<HistoryOutlinedIcon />}
                                        onClick={() => void handleRevertDescribeImage()}
                                        disabled={
                                            Boolean(actionKey) ||
                                            recreatingDescribeImage ||
                                            revertingDescribeImage ||
                                            !hasCachedOriginalDescribeImage
                                        }
                                    >
                                        {revertingDescribeImage ? 'Reverting...' : 'Revert Image'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleDownloadImage()}
                                        disabled={
                                            Boolean(actionKey) ||
                                            recreatingDescribeImage ||
                                            revertingDescribeImage ||
                                            !describeImageDownloadUrl
                                        }
                                    >
                                        Open Image
                                    </Button>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    {describeImageFile
                                        ? `Selected file: ${describeImageFile.name}`
                                        : 'Upload a JPG, JPEG, PNG, or WEBP image before saving.'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Generate Image recreates the current Describe Image from the stored source and replaces the existing file in place. Revert Image restores the original file cached before the first regeneration.
                                </Typography>
                                {describeImagePreviewUrl ? (
                                    <Box
                                        component="img"
                                        src={describeImagePreviewUrl}
                                        alt={describeImageFile?.name ?? 'Selected describe image'}
                                        sx={{
                                            display: 'block',
                                            width: '100%',
                                            maxWidth: 420,
                                            borderRadius: 1,
                                            border: (theme) => `1px solid ${theme.palette.divider}`,
                                        }}
                                    />
                                ) : null}
                            </Stack>
                        ) : null}
                        {shouldShowVoiceSection ? (
                            <Stack spacing={1.5}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                                    <Typography variant="subtitle2">Voices</Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                        {canManageVoiceGeneration && isVoiceRequiredExamTaskType(String(match?.apiType ?? record?.examTaskType ?? '').trim()) ? (
                                            <>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<AutoFixHighOutlinedIcon />}
                                                    disabled={Boolean(actionKey)}
                                                    onClick={() => void handleRegenerateLiveVoice()}
                                                >
                                                    {actionKey === 'regenerate-voice' ? 'Queueing Audio...' : 'Regenerate Audio'}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    disabled={Boolean(actionKey) || voiceRegenerationStatusLoading}
                                                    onClick={() => void handleRefreshLiveVoiceStatus()}
                                                >
                                                    {voiceRegenerationStatusLoading || voiceRegenerationStatusPolling
                                                        ? 'Refreshing Audio Status...'
                                                        : 'Refresh Audio Status'}
                                                </Button>
                                            </>
                                        ) : null}
                                        {voiceRecords.length > 0 ? (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                startIcon={<DeleteOutlineIcon />}
                                                disabled={Boolean(actionKey)}
                                                onClick={() => void handleDeleteAllLiveVoices()}
                                            >
                                                {actionKey === 'delete-voices' ? 'Deleting Audio...' : 'Delete All Audio'}
                                            </Button>
                                        ) : null}
                                    </Stack>
                                </Stack>
                                {voiceRegenerationStatusError ? <Alert severity="error">{voiceRegenerationStatusError}</Alert> : null}
                                {voiceRegenerationStatus ? (
                                    <Alert severity={voiceRegenerationStatus.requestStatus === 'failed' ? 'error' : voiceRegenerationStatus.isTerminal ? 'success' : 'info'}>
                                        <Stack spacing={0.5}>
                                            <Typography variant="body2">
                                                {`Audio status: ${formatStatusLabel(voiceRegenerationStatus.requestStatus)}`}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {`Attempts: ${voiceRegenerationStatus.attemptCount} · Requested: ${formatLocalDateTime(voiceRegenerationStatus.requestedAt)}`}
                                            </Typography>
                                            {voiceRegenerationStatus.lastError ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    {`Last error: ${voiceRegenerationStatus.lastError}`}
                                                </Typography>
                                            ) : null}
                                        </Stack>
                                    </Alert>
                                ) : null}
                                {voiceRecords.length > 0 ? (
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gap: 1.5,
                                            gridTemplateColumns: {
                                                xs: '1fr',
                                                md: 'repeat(2, minmax(0, 1fr))',
                                            },
                                        }}
                                    >
                                        {voiceRecords.map((voice) => {
                                            const voiceUrl = getVoiceUrl(voice);
                                            const voiceLabel = String(voice.voiceLabel ?? voice.label ?? voice.name ?? voice.StorageFilename ?? 'Voice');
                                            const languageTag = String(voice.languageTag ?? voice.language_tag ?? '').trim();
                                            const genderCode = String(voice.genderCode ?? voice.gender_code ?? '').trim();

                                            return (
                                                <Card key={String(voice.examTaskMediaVoiceId ?? voice.id ?? voiceUrl)} variant="outlined">
                                                    <CardContent>
                                                        <Stack spacing={1.25}>
                                                            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                                                                <Typography variant="subtitle2" sx={{ wordBreak: 'break-word', flex: 1, minWidth: 0 }}>
                                                                    {voiceLabel}
                                                                </Typography>
                                                                <Button
                                                                    variant="text"
                                                                    color="error"
                                                                    size="small"
                                                                    startIcon={<DeleteOutlineIcon />}
                                                                    disabled={Boolean(actionKey)}
                                                                    onClick={() => void handleDeleteLiveVoice(voice)}
                                                                >
                                                                    {actionKey === `delete-voice:${String(voice.examTaskMediaVoiceId ?? voice.id ?? '').trim()}`
                                                                        ? 'Deleting...'
                                                                        : 'Delete'}
                                                                </Button>
                                                            </Stack>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {[languageTag, genderCode].filter(Boolean).join(' · ') || 'Voice file'}
                                                            </Typography>
                                                            {voiceUrl ? (
                                                                <MediaPreview
                                                                    url={voiceUrl}
                                                                    title={voiceLabel}
                                                                    width="100%"
                                                                    height={56}
                                                                    showUrl
                                                                    showWaveform
                                                                />
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Voice URL unavailable
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                    </Box>
                                ) : (
                                    <Alert severity="info">
                                        No audio is currently attached to this exam task. Use Regenerate Audio to queue a new voice build.
                                    </Alert>
                                )}
                            </Stack>
                        ) : null}
                        <TextField
                            label="Discovery Tags"
                            value={liveForm.discoveryTags}
                            onChange={(event) => handleLiveFieldChange('discoveryTags', event.target.value)}
                            helperText="Comma-separated tags are sent as text unless you enter valid JSON."
                            multiline
                            minRows={2}
                            fullWidth
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={liveForm.isPremiumContent}
                                    onChange={(event) => handleLiveFieldChange('isPremiumContent', event.target.checked)}
                                />
                            }
                            label="Premium Content"
                        />
                        <TextField
                            label="Answer Choices"
                            value={liveForm.answerChoices}
                            onChange={(event) => handleLiveFieldChange('answerChoices', event.target.value)}
                            helperText="Optional. Plain text is sent as text; valid JSON is parsed first."
                            multiline
                            minRows={4}
                            fullWidth
                        />
                        <TextField
                            label="Config Settings"
                            value={liveForm.configSettings}
                            onChange={(event) => handleLiveFieldChange('configSettings', event.target.value)}
                            helperText="Optional JSON object or array. Leave empty to send null."
                            multiline
                            minRows={6}
                            fullWidth
                        />
                        <TextField
                            label="Segments"
                            value={liveForm.segments}
                            onChange={(event) => handleLiveFieldChange('segments', event.target.value)}
                            helperText="JSON array matching the live edit API payload."
                            multiline
                            minRows={10}
                            fullWidth
                        />
                    </Stack>

                    <Card variant="outlined">
                        <CardContent>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="h6">Published Files</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Preview the published image and voice files currently linked to this exam task.
                                    </Typography>
                                </Box>

                                {liveFilesLoading ? (
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <CircularProgress size={20} />
                                        <Typography variant="body2" color="text.secondary">
                                            Loading published files...
                                        </Typography>
                                    </Stack>
                                ) : liveFilesError ? (
                                    <Alert severity="warning">{liveFilesError}</Alert>
                                ) : !publishedImageUrl && publishedVoicePreviews.length === 0 ? (
                                    <Alert severity="info">No published files are available yet for this exam task.</Alert>
                                ) : (
                                    <Stack spacing={2}>
                                        {publishedImageUrl ? (
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Stack spacing={1.25}>
                                                        <Typography variant="subtitle2">Published Image</Typography>
                                                        {liveFiles?.publishedImageBucketName ? (
                                                            <Typography variant="body2" color="text.secondary">
                                                                Bucket: {liveFiles.publishedImageBucketName}
                                                            </Typography>
                                                        ) : null}
                                                        <MediaPreview
                                                            url={publishedImageUrl}
                                                            title={liveForm.displayTitle || currentExamTaskLabel || 'Published Image'}
                                                            width="100%"
                                                            height={220}
                                                            showUrl
                                                        />
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        ) : null}

                                        {publishedVoicePreviews.length > 0 ? (
                                            <Stack spacing={1.5}>
                                                <Box>
                                                    <Typography variant="subtitle2">Published Audio</Typography>
                                                    {liveFiles?.publishedVoiceBucketName ? (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Bucket: {liveFiles.publishedVoiceBucketName}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gap: 1.5,
                                                        gridTemplateColumns: {
                                                            xs: '1fr',
                                                            md: 'repeat(2, minmax(0, 1fr))',
                                                        },
                                                    }}
                                                >
                                                    {publishedVoicePreviews.map((voice) => (
                                                        <Card key={voice.id} variant="outlined">
                                                            <CardContent>
                                                                <Stack spacing={1.25}>
                                                                    <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
                                                                        {voice.label}
                                                                    </Typography>
                                                                    <MediaPreview
                                                                        url={voice.previewUrl}
                                                                        title={voice.label}
                                                                        width="100%"
                                                                        height={56}
                                                                        showUrl
                                                                        showWaveform
                                                                    />
                                                                </Stack>
                                                            </CardContent>
                                                        </Card>
                                                    ))}
                                                </Box>
                                            </Stack>
                                        ) : null}
                                    </Stack>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setLiveForm(buildLiveExamTaskFormState(record));
                                setDescribeImageFile(null);
                            }}
                            disabled={Boolean(actionKey)}
                        >
                            Reset
                        </Button>
                        <Button
                            variant="contained"
                            color={primaryAction?.color ?? 'primary'}
                            disabled={Boolean(actionKey) || !primaryAction?.actionPath}
                            onClick={() => void handleLiveSave()}
                        >
                            {actionKey === primaryAction?.key ? primaryAction?.pendingLabel : primaryAction?.label}
                        </Button>
                        {getVisibleExamTaskWorkflowActions(workflow, isExamTaskActive(record)).map((action) => (
                            <Button
                                key={action.key}
                                variant={action.color === 'error' ? 'outlined' : 'contained'}
                                color={action.color}
                                sx={action.key === 'publish' ? publishActionButtonSx : undefined}
                                disabled={Boolean(actionKey) || !action.actionPath}
                                onClick={() => void handleLiveStateAction(action)}
                            >
                                {actionKey === action.key ? action.pendingLabel : action.label}
                            </Button>
                        ))}
                    </Stack>
                </>
            ) : (
                <>
                    <TableContainer
                        sx={{
                            width: '100%',
                            overflowX: 'auto',
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                        }}
                    >
                        <Table size="small" sx={{ minWidth: 900 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700, width: 240 }}>
                                        Field
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rowEntries.map(([key, value]) => (
                                    <TableRow key={key} hover>
                                        <TableCell sx={{ verticalAlign: 'top', fontWeight: 600 }}>
                                            {formatColumnLabel(key)}
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top' }}>
                                            {isMediaUrlField(key, value) ? (
                                                <MediaPreview
                                                    url={isDescribeImageRecord(record, match?.apiType) ? resolveDescribeImageUrl(value) : value}
                                                    title={formatColumnLabel(key)}
                                                    width="100%"
                                                    height={220}
                                                    showUrl
                                                />
                                            ) : (
                                                <Box
                                                    component="pre"
                                                    sx={{
                                                        m: 0,
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        fontFamily: 'inherit',
                                                    }}
                                                >
                                                    {formatValue(value)}
                                                </Box>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {voiceRecords.length > 0 ? (
                        <Stack spacing={1.5}>
                            <Typography variant="subtitle2">Voices</Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 1.5,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: 'repeat(2, minmax(0, 1fr))',
                                    },
                                }}
                            >
                                {voiceRecords.map((voice) => {
                                    const voiceUrl = getVoiceUrl(voice);
                                    const voiceLabel = String(voice.voiceLabel ?? voice.label ?? voice.name ?? voice.StorageFilename ?? 'Voice');
                                    const languageTag = String(voice.languageTag ?? voice.language_tag ?? '').trim();
                                    const genderCode = String(voice.genderCode ?? voice.gender_code ?? '').trim();

                                    return (
                                        <Card key={String(voice.examTaskMediaVoiceId ?? voice.id ?? voiceUrl)} variant="outlined">
                                            <CardContent>
                                                <Stack spacing={1.25}>
                                                    <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
                                                        {voiceLabel}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {[languageTag, genderCode].filter(Boolean).join(' · ') || 'Voice file'}
                                                    </Typography>
                                                    {voiceUrl ? (
                                                        <MediaPreview
                                                            url={voiceUrl}
                                                            title={voiceLabel}
                                                            width="100%"
                                                            height={56}
                                                            showUrl
                                                            showWaveform
                                                        />
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Voice URL unavailable
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Box>
                        </Stack>
                    ) : null}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
                        {workflow.actions.map((action) => (
                            <Button
                                key={action.key}
                                variant="contained"
                                color={action.color}
                                disabled={Boolean(actionKey) || !action.actionPath}
                                onClick={() => void handleReviewAction(action)}
                            >
                                {actionKey === action.key ? action.pendingLabel : action.label}
                            </Button>
                        ))}
                    </Stack>

                </>
            )}

            {record ? (
                <AiSourceArtifactPanel
                    examTaskId={String(record.id)}
                    examName={examName}
                    examTaskType={String(record.examTaskType ?? match?.apiType ?? '')}
                />
            ) : null}

            {record && !useGenericAiSourceArtifactPanel && isDescribeImageRecord(record, match?.apiType) ? (
                <Card variant="outlined">
                    <CardContent>
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="h6">Describe Image Blueprint</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Generation is manual. Review the image and factual blueprint before saving it as approved for scoring.
                                </Typography>
                            </Box>

                            {blueprintError ? <Alert severity="error">{blueprintError}</Alert> : null}
                            {blueprintLoading ? (
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <CircularProgress size={20} />
                                    <Typography variant="body2">Loading blueprint...</Typography>
                                </Stack>
                            ) : !blueprintDetail?.artifact || !blueprintDraft ? (
                                <Stack spacing={1.5} alignItems="flex-start">
                                    <Alert severity="info">No blueprint exists for this image.</Alert>
                                    <Button
                                        variant="contained"
                                        startIcon={<AutoFixHighOutlinedIcon />}
                                        disabled={blueprintAction !== null}
                                        onClick={() => void handleGenerateBlueprint(false)}
                                    >
                                        {blueprintAction === 'generate' ? 'Generating Blueprint...' : 'Generate Blueprint'}
                                    </Button>
                                </Stack>
                            ) : (
                                <>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gap: 2,
                                            gridTemplateColumns: { xs: '1fr', lg: 'minmax(300px, 0.8fr) minmax(0, 1.2fr)' },
                                        }}
                                    >
                                        <Stack spacing={1.5}>
                                            <MediaPreview
                                                url={describeImageDownloadUrl}
                                                title={String(record.displayTitle ?? currentExamTaskLabel ?? 'Describe Image')}
                                                width="100%"
                                                height={320}
                                                showUrl
                                            />
                                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                                Status: {blueprintDetail.artifact.status} · Model: {blueprintDetail.artifact.generatorModel}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                                Source: {blueprintDetail.artifact.sourceSha256} · Prompt: {blueprintDetail.artifact.promptVersion} · Schema: {blueprintDetail.artifact.schemaVersion}
                                            </Typography>
                                        </Stack>

                                        <Stack spacing={1.5}>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                                <TextField
                                                    select
                                                    label="Image Type"
                                                    value={blueprintDraft.imageType}
                                                    onChange={(event) => setBlueprintDraft((current) => current
                                                        ? {
                                                            ...current,
                                                            imageType: event.target.value,
                                                            barArrangement: event.target.value === 'bar_chart'
                                                                ? (current.barArrangement === 'not_applicable' ? 'ambiguous' : current.barArrangement)
                                                                : 'not_applicable',
                                                        }
                                                        : current)}
                                                    fullWidth
                                                >
                                                    {describeImageBlueprintTypes.map((imageType) => (
                                                        <MenuItem key={imageType} value={imageType}>{imageType}</MenuItem>
                                                    ))}
                                                </TextField>
                                                <TextField
                                                    select
                                                    label="Bar Arrangement"
                                                    value={blueprintDraft.barArrangement}
                                                    disabled={blueprintDraft.imageType !== 'bar_chart'}
                                                    onChange={(event) => setBlueprintDraft((current) => current
                                                        ? { ...current, barArrangement: event.target.value }
                                                        : current)}
                                                    fullWidth
                                                >
                                                    {['not_applicable', 'single_series', 'grouped', 'stacked', 'grouped_and_stacked', 'ambiguous'].map((arrangement) => (
                                                        <MenuItem key={arrangement} value={arrangement}>{arrangement}</MenuItem>
                                                    ))}
                                                </TextField>
                                                <TextField
                                                    label="Confidence"
                                                    type="number"
                                                    value={blueprintDraft.confidence}
                                                    inputProps={{ min: 0, max: 1, step: 0.01 }}
                                                    onChange={(event) => setBlueprintDraft((current) => current
                                                        ? { ...current, confidence: Number(event.target.value) }
                                                        : current)}
                                                    fullWidth
                                                />
                                            </Stack>
                                            <TextField
                                                label="Overview"
                                                value={blueprintDraft.overview}
                                                onChange={(event) => setBlueprintDraft((current) => current ? { ...current, overview: event.target.value } : current)}
                                                multiline
                                                minRows={3}
                                                fullWidth
                                            />
                                            {([
                                                ['keyElements', 'Key Elements'],
                                                ['relationships', 'Relationships'],
                                                ['trends', 'Trends'],
                                                ['comparisons', 'Comparisons'],
                                                ['unsupportedClaimWarnings', 'Unsupported-claim Warnings'],
                                            ] as const).map(([field, label]) => (
                                                <TextField
                                                    key={field}
                                                    label={label}
                                                    value={blueprintDraft[field].join('\n')}
                                                    onChange={(event) => setBlueprintList(field, event.target.value)}
                                                    helperText="One factual item per line."
                                                    multiline
                                                    minRows={2}
                                                    fullWidth
                                                />
                                            ))}
                                            <Alert severity="info">
                                                Blueprint v2 does not permit inferred implications. Record only visible facts and unsupported-claim warnings.
                                            </Alert>

                                            <Stack spacing={1}>
                                                <Typography variant="subtitle2">Numeric Values</Typography>
                                                {blueprintDraft.values.map((value, index) => (
                                                    <Stack key={`${index}-${value.label}`} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                                        <TextField
                                                            label="Label"
                                                            value={value.label}
                                                            onChange={(event) => setBlueprintDraft((current) => {
                                                                if (!current) return current;
                                                                const values = current.values.map((item, itemIndex) => itemIndex === index
                                                                    ? { ...item, label: event.target.value }
                                                                    : item);
                                                                return { ...current, values };
                                                            })}
                                                            fullWidth
                                                        />
                                                        <TextField
                                                            label="Value"
                                                            type="number"
                                                            value={value.value}
                                                            onChange={(event) => setBlueprintDraft((current) => {
                                                                if (!current) return current;
                                                                const values = current.values.map((item, itemIndex) => itemIndex === index
                                                                    ? { ...item, value: Number(event.target.value) }
                                                                    : item);
                                                                return { ...current, values };
                                                            })}
                                                            fullWidth
                                                        />
                                                        <TextField
                                                            label="Unit"
                                                            value={value.unit ?? ''}
                                                            onChange={(event) => setBlueprintDraft((current) => {
                                                                if (!current) return current;
                                                                const values = current.values.map((item, itemIndex) => itemIndex === index
                                                                    ? { ...item, unit: event.target.value.trim() || null }
                                                                    : item);
                                                                return { ...current, values };
                                                            })}
                                                            fullWidth
                                                        />
                                                        <Button
                                                            color="error"
                                                            onClick={() => setBlueprintDraft((current) => current
                                                                ? { ...current, values: current.values.filter((_, itemIndex) => itemIndex !== index) }
                                                                : current)}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Stack>
                                                ))}
                                                <Button
                                                    variant="text"
                                                    onClick={() => setBlueprintDraft((current) => current
                                                        ? { ...current, values: [...current.values, { label: '', value: 0, unit: null }] }
                                                        : current)}
                                                >
                                                    Add Numeric Value
                                                </Button>
                                            </Stack>
                                            <TextField
                                                label="Admin Notes"
                                                value={blueprintNotes}
                                                onChange={(event) => setBlueprintNotes(event.target.value)}
                                                multiline
                                                minRows={2}
                                                fullWidth
                                            />
                                        </Stack>
                                    </Box>

                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
                                        <Button
                                            variant="outlined"
                                            disabled={blueprintAction !== null}
                                            onClick={() => void handleGenerateBlueprint(true)}
                                        >
                                            {blueprintAction === 'regenerate' ? 'Regenerating...' : 'Regenerate Blueprint'}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            disabled={blueprintAction !== null}
                                            onClick={() => void handleSaveBlueprint()}
                                        >
                                            {blueprintAction === 'save' ? 'Saving...' : 'Save as Approved'}
                                        </Button>
                                    </Stack>
                                </>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            ) : null}

            {record
                && !useGenericAiSourceArtifactPanel
                && examName === 'Academic'
                && String(record.examTaskType ?? match?.apiType ?? '').replace(/[^a-z]/gi, '').toLowerCase() === 'summarizegroupdiscussion' ? (
                <SummarizeGroupDiscussionBlueprintPanel
                    examTaskId={String(record.id)}
                    examName={examName}
                    promptBody={String(record.promptBody ?? '')}
                />
            ) : null}

            <Dialog
                open={generateContentDialogOpen}
                onClose={handleCloseGenerateContentDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Generate Content</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            The current Prompt Body is sent to the admin rewrite endpoint. Review the generated title and prompt body before applying them to the live exam task form.
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={generateContentIsConservative}
                                    onChange={(event) => {
                                        setGenerateContentIsConservative(event.target.checked);
                                        setGeneratedContent(null);
                                        setGeneratedContentError(null);
                                    }}
                                    disabled={generatingContent}
                                />
                            }
                            label="Conservative rewrite"
                        />
                        <TextField
                            label="Current Prompt Body"
                            value={liveForm?.promptBody ?? ''}
                            multiline
                            minRows={4}
                            fullWidth
                            slotProps={{ input: { readOnly: true } }}
                        />
                        {generatingContent ? (
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2" color="text.secondary">
                                    Generating replacement content...
                                </Typography>
                            </Stack>
                        ) : null}
                        {generatedContentError ? <Alert severity="error">{generatedContentError}</Alert> : null}
                        {generatedContent ? (
                            <>
                                <TextField
                                    label="Generated Title"
                                    value={generatedContent.suggestedTitle}
                                    fullWidth
                                    slotProps={{ input: { readOnly: true } }}
                                />
                                <TextField
                                    label="Generated Prompt Body"
                                    value={generatedContent.rewrittenParagraph}
                                    multiline
                                    minRows={6}
                                    fullWidth
                                    slotProps={{ input: { readOnly: true } }}
                                />
                            </>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseGenerateContentDialog} disabled={generatingContent}>
                        Close
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={generatingContent || !liveForm?.promptBody.trim()}
                        onClick={() => void handleGenerateContent()}
                    >
                        {generatingContent ? 'Generating...' : generatedContent ? 'Generate Again' : 'Generate'}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!generatedContent || generatingContent}
                        onClick={handleApplyGeneratedContent}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={copyToCoreDialogOpen}
                onClose={() => setCopyToCoreDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Copy to Core</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                        <Typography variant="body1">
                            {record
                                ? `Are you sure you want to copy ${String(record.displayTitle ?? record.id)} to Core?`
                                : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            If you click Yes, a Core draft exam task will be created from this Academic exam task.
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCopyToCoreDialogOpen(false)} disabled={Boolean(actionKey)}>
                        No
                    </Button>
                    <Button
                        variant="contained"
                        disabled={Boolean(actionKey) || !record}
                        onClick={() => {
                            setCopyToCoreDialogOpen(false);
                            void handleCopyAcademicToCore();
                        }}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={pendingPublishAction !== null}
                onClose={() => setPendingPublishAction(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{`Publish to ${publishTarget.title}`}</DialogTitle>
                <DialogContent>
                    <Stack spacing={1.5} sx={{ pt: 1 }}>
                        <Typography variant="body1">
                            {record
                                ? `Are you sure you want to publish ${String(record.displayTitle ?? record.id)} to ${publishTarget.noun}?`
                                : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {`This exam task will be published to ${publishTarget.noun} only if you click Yes.`}
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingPublishAction(null)} disabled={Boolean(actionKey)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        sx={publishActionButtonSx}
                        disabled={Boolean(actionKey) || !pendingPublishAction}
                        onClick={() => {
                            if (!pendingPublishAction) {
                                return;
                            }

                            const nextPendingAction = pendingPublishAction;
                            setPendingPublishAction(null);
                            void runLiveStateAction(nextPendingAction);
                        }}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}

export function ExamTaskDetailPage({ workflow: workflowId = 'review' }: ExamTaskDetailPageProps) {
    const workflow = getExamTaskWorkflow(workflowId);

    return (
        <Card sx={{ width: '100%' }}>
            <Title title={workflow.detailTitle} />
            <CardContent sx={{ width: '100%' }}>
                <ExamTaskDetailContent workflow={workflow} />
            </CardContent>
        </Card>
    );
}
